import React, { useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import VisitList from "./pages/VisitList";
import VisitForm from "./pages/VisitForm";
import ChecklistForm from "./pages/ChecklistForm";
import ActionPlanPage from "./pages/ActionPlanPage";
import SummaryPage from "./pages/SummaryPage";
import AdminPage from "./pages/AdminPage";
import UsersPage from "./pages/UsersPage";
import RegisterPage from "./pages/RegisterPage";
import StoresPage from "./pages/StoresPage";
import DirectorDashboard from "./pages/DirectorDashboard";
import LoginPage from "./pages/LoginPage";
import { api } from "./utils/api";
import "./styles.css";

function RequireAuth({ children }) {
  const user = api.getCurrentUser();
  const token = api.getToken();
  if (!user || !token) return <Navigate to="/login" replace />;
  return children;
}

function RequireDirectorOrDashboard({ children }) {
  const user = api.getCurrentUser();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "director") return children;
  if (user.role === "admin" && user.dashboard_access) return children;
  return <Navigate to="/" replace />;
}

function RequireAdmin({ children }) {
  const user = api.getCurrentUser();
  if (!user || !["admin", "director"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function DrawerMenu({ open, onClose }) {
  const user = api.getCurrentUser();
  const isAdmin = user?.role === "admin" || user?.role === "director";
  const isAdminOrDir = user?.role === "admin" || user?.role === "director";
  const [pendingCount, setPendingCount] = React.useState(0);

  React.useEffect(() => {
    if (isAdminOrDir && api.getToken()) {
      api.getPendingCount().then(r => setPendingCount(r.count)).catch(() => {});
      const interval = setInterval(() => {
        if (api.getToken()) {
          api.getPendingCount().then(r => setPendingCount(r.count)).catch(() => {});
        }
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [isAdminOrDir]);

  const links = [
    { to: "/", icon: "⊞", label: "Tableau de bord", always: true },
    { to: "/visits", icon: "◈", label: "Visites", always: true },
    { to: "/admin", icon: "⚙", label: "Parametrage", adminOnly: true },
    { to: "/users", icon: "👥", label: "Utilisateurs", adminOnly: true },
    { to: "/stores", icon: "🏪", label: "Boutiques", adminOnly: true },
    { to: "/director", icon: "📊", label: "Dir. Dashboard", directorOnly: true },
  ].filter(l => l.always || (l.adminOnly && isAdmin) || (l.directorOnly && (user?.role === "director" || (user?.role === "admin" && user?.dashboard_access))));

  return (
    <>
      <div className={`drawer-overlay${open ? " active" : ""}`} onClick={onClose} />
      <nav className={`drawer${open ? " open" : ""}`}>
        <div className="drawer-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="brand-mark">L</span>
            <div>
              <div className="brand-name">LUXOLOR</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.45)", marginTop: 1 }}>
                {isAdmin ? "Administrateur" : "Utilisateur"}
              </div>
            </div>
          </div>
          <button className="drawer-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: "8px 0", fontSize: 10, color: "rgba(255,255,255,.4)", paddingLeft: 20, letterSpacing: ".08em", textTransform: "uppercase", marginTop: 8 }}>
          Navigation
        </div>

        <div className="drawer-links">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"}
              className={({ isActive }) => "drawer-link" + (isActive ? " active" : "")}
              onClick={onClose}>
              <span className="drawer-icon">{l.icon}</span>
              <span style={{ flex: 1 }}>{l.label}</span>
              {l.to === "/users" && pendingCount > 0 && (
                <span style={{ background: "var(--red)", color: "white", borderRadius: "50%", width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{pendingCount}</span>
              )}
            </NavLink>
          ))}
        </div>

        <div className="drawer-footer">
          <div className="drawer-user">
            <div className="drawer-avatar">{(user?.full_name || user?.username || "U")[0].toUpperCase()}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "white" }}>{user?.full_name || user?.username}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.45)" }}>@{user?.username}</div>
            </div>
          </div>
          <button className="drawer-logout" onClick={() => api.logout()}>
            <span>⎋</span> Deconnexion
          </button>
        </div>
      </nav>
    </>
  );
}

function DesktopSidebar() {
  const user = api.getCurrentUser();
  const isAdmin = user?.role === "admin" || user?.role === "director";
  const isAdminOrDir = user?.role === "admin" || user?.role === "director";
  const [pendingCount, setPendingCount] = React.useState(0);

  React.useEffect(() => {
    if (isAdminOrDir && api.getToken()) {
      api.getPendingCount().then(r => setPendingCount(r.count)).catch(() => {});
      const interval = setInterval(() => {
        if (api.getToken()) {
          api.getPendingCount().then(r => setPendingCount(r.count)).catch(() => {});
        }
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [isAdminOrDir]);

  const links = [
    { to: "/", icon: "⊞", label: "Tableau de bord", always: true },
    { to: "/visits", icon: "◈", label: "Visites", always: true },
    { to: "/admin", icon: "⚙", label: "Parametrage", adminOnly: true },
    { to: "/users", icon: "👥", label: "Utilisateurs", adminOnly: true },
    { to: "/stores", icon: "🏪", label: "Boutiques", adminOnly: true },
    { to: "/director", icon: "📊", label: "Dir. Dashboard", directorOnly: true },
  ].filter(l => l.always || (l.adminOnly && isAdmin) || (l.directorOnly && (user?.role === "director" || (user?.role === "admin" && user?.dashboard_access))));

  return (
    <nav className="sidebar">
      <div className="sidebar-brand">
        <span className="brand-mark">L</span>
        <span className="brand-name">LUXOLOR</span>
      </div>
      <div className="sidebar-links">
        {links.map(l => (
          <NavLink key={l.to} to={l.to} end={l.to === "/"}
            className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
            <span className="nav-icon">{l.icon}</span>
            <span className="nav-label" style={{ flex: 1 }}>{l.label}</span>
            {l.to === "/users" && pendingCount > 0 && (
              <span style={{ background: "var(--red)", color: "white", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>{pendingCount}</span>
            )}
          </NavLink>
        ))}
      </div>
      <div className="sidebar-footer">
        <div style={{ fontSize: 11, color: "rgba(255,255,255,.4)", marginBottom: 2 }}>
          {isAdmin ? "Administrateur" : "Utilisateur"}
        </div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,.65)", marginBottom: 10 }}>
          {user?.full_name || user?.username}
        </div>
        <button onClick={() => api.logout()} style={{
          background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.12)",
          color: "rgba(255,255,255,.7)", padding: "8px 12px",
          borderRadius: 8, cursor: "pointer", fontSize: 12, width: "100%",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6
        }}>
          <span>⎋</span> Deconnexion
        </button>
      </div>
    </nav>
  );
}

function MobileBottomNav() {
  const user = api.getCurrentUser();
  const isAdmin = user?.role === "admin" || user?.role === "director";
  const links = [
    { to: "/", icon: "⊞", label: "Accueil", always: true },
    { to: "/visits", icon: "◈", label: "Visites", always: true },
    { to: "/admin", icon: "⚙", label: "Param", adminOnly: true },
    { to: "/users", icon: "👥", label: "Users", adminOnly: true },
  ].filter(l => l.always || (l.adminOnly && isAdmin) || (l.directorOnly && (user?.role === "director" || (user?.role === "admin" && user?.dashboard_access))));

  return (
    <nav className="mobile-bottomnav">
      {links.map(l => (
        <NavLink key={l.to} to={l.to} end={l.to === "/"}
          className={({ isActive }) => "bottomnav-link" + (isActive ? " active" : "")}>
          <span className="bottomnav-icon">{l.icon}</span>
          <span className="bottomnav-label">{l.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="app-shell">
      <DesktopSidebar />
      <DrawerMenu open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <div className="app-body">
        <header className="mobile-header">
          <button className="hamburger-btn" onClick={() => setDrawerOpen(true)}>
            <span /><span /><span />
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="brand-mark" style={{ width: 30, height: 30, fontSize: 15 }}>L</span>
            <span style={{ color: "var(--navy)", fontWeight: 700, fontSize: 16, letterSpacing: ".04em" }}>LUXOLOR</span>
          </div>
          <div style={{ width: 44 }} />
        </header>

        <main className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/visits" element={<VisitList />} />
            <Route path="/visits/new" element={<VisitForm />} />
            <Route path="/visits/:id/edit" element={<VisitForm />} />
            <Route path="/visits/:id/checklist" element={<ChecklistForm />} />
            <Route path="/visits/:id/actions" element={<ActionPlanPage />} />
            <Route path="/visits/:id/summary" element={<SummaryPage />} />
            <Route path="/admin" element={<RequireAdmin><AdminPage /></RequireAdmin>} />
            <Route path="/users" element={<RequireAdmin><UsersPage /></RequireAdmin>} />
            <Route path="/stores" element={<RequireAdmin><StoresPage /></RequireAdmin>} />
            <Route path="/director" element={<RequireDirectorOrDashboard><DirectorDashboard /></RequireDirectorOrDashboard>} />
          </Routes>
        </main>

      </div>
    </div>
  );
}

export default function App() {
  React.useEffect(() => {
    if (api.getToken()) {
      api.refreshUser().catch(() => {});
    }
  }, []);
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/*" element={<RequireAuth><AppLayout /></RequireAuth>} />
      </Routes>
    </BrowserRouter>
  );
}
